import React, { useEffect, useState } from 'react';
import SaveFeature from './SaveFeature';
import ReportFeature from './ReportFeature';

interface DialogueBoxProps {
  characterName: string;
  characterImage?: string;
  text: string;
  visible?: boolean;
  countdownSeconds?: number | null;
  countdownTotalSeconds?: number;
  onSaveClick?: () => void;
  isSaved?: boolean;
  onReportClick?: () => void;
}

const DialogueBox: React.FC<DialogueBoxProps> = ({
  characterName,
  text,
  visible = true,
  countdownSeconds = null,
  countdownTotalSeconds = 50,
  onSaveClick,
  isSaved = false,
  onReportClick,
}) => {
  // const [displayedText, setDisplayedText] = useState('');
  // const [isTyping, setIsTyping] = useState(false);

  // useEffect(() => {
  //   setDisplayedText('');
  //   setIsTyping(true);
  //   let i = 0;
  //   const interval = setInterval(() => {
  //     if (i < text.length) {
  //       setDisplayedText(text.slice(0, i + 1));
  //       i++;
  //     } else {
  //       setIsTyping(false);
  //       clearInterval(interval);
  //     }
  //   }, 25); // speed in ms — lower = faster

  //   return () => clearInterval(interval);
  // }, [text]);

  const hasCountdown = typeof countdownSeconds === 'number';
  const totalSeconds = Math.max(1, countdownTotalSeconds);
  const clampedSeconds = hasCountdown ? Math.max(0, Math.floor(countdownSeconds ?? 0)) : 0;
  const countdownRatio = hasCountdown
    ? Math.max(0, Math.min(1, clampedSeconds / totalSeconds))
    : 1;
  const countdownColor = !hasCountdown
    ? '#A5FFF5'
    : countdownRatio <= 0
      ? '#CBD5E1'
      : countdownRatio <= 0.1
        ? '#FCA5A5'
        : countdownRatio <= 0.33
          ? '#FBBF24'
          : countdownRatio <= 0.66
            ? '#FDE047'
            : '#86EFAC';

  return (
    <div
      className={`relative flex flex-col max-w-full overflow-hidden px-4 py-3 sm:px-6 sm:py-4 transition-opacity duration-300 ease-in-out dialogue-box-container ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{
        padding: 'clamp(8px, 1.5vh, 16px) clamp(12px, 2vw, 24px)',
        width: 'clamp(280px, min(57vw, 80vh), 820px)',
        borderRadius: 'clamp(12px, 2vw, 22px)',
        border: '1px solid #2E3A6B',
        background: 'linear-gradient(180deg, rgba(39, 49, 93, 0.8) 0%, rgba(41, 50, 93, 0.8) 100%)',
        boxShadow: '0 8px 20px 0 rgba(0, 0, 0, 0.28)',
      }}
    >
      <div className="absolute left-5 right-5 top-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent blur-[0.5px] sm:left-6 sm:right-6" />

      <div className="relative z-10 flex items-center justify-between gap-2 mb-2 sm:mb-0">
        <div className="flex items-center gap-2 min-w-0 pr-3">

          <h3
            className="dialogue-character-name"
            style={{
              color: '#A5FFF5',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 'clamp(12px, min(3vw, 3.5vh), 28px)',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            {characterName}:
          </h3>
        </div>
        <div className="shrink-0 flex items-center gap-3 py-0.5 mt-1">
          {hasCountdown && (
            <div aria-live="polite" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* stem */}
              <div style={{
                width: '6px', height: '2px',
                borderRadius: '2px 2px 0 0',
                background: countdownColor,
                boxShadow: `0 0 6px ${countdownColor}88`,
                transition: 'background 0.5s ease',
              }} />
              <svg
                width="clamp(28px, 3.5vh, 42px)"
                height="clamp(28px, 3.5vh, 42px)"
                viewBox="0 0 42 42"
                style={{ display: 'block', overflow: 'visible' }}
              >
                <circle cx="21" cy="21" r="17" fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.08)" strokeWidth="2.5" />
                <circle
                  cx="21" cy="21" r="17"
                  fill="none"
                  stroke={countdownColor}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 17}`}
                  strokeDashoffset={`${2 * Math.PI * 17 * (1 - countdownRatio)}`}
                  transform="rotate(-90 21 21)"
                  style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.5s ease' }}
                />
                <text
                  x="21" y="21"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={countdownColor}
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 'clamp(9px, 1.5vh, 13px)',
                    fontWeight: 700,
                    transition: 'fill 0.5s ease'
                  }}>
                  {clampedSeconds}
                </text>
              </svg>
            </div>
          )}
          <ReportFeature onClick={onReportClick} />
          <SaveFeature onClick={onSaveClick} isSaved={isSaved} />
        </div>
      </div>

      <div className="relative z-10 mb-1 h-px w-full bg-cyan-400/35 shadow-[0_0_10px_rgba(34,211,238,0.25)]" />

      <p
        className="relative z-10 max-w-[95%] dialogue-question-text"
        style={{
          color: '#FFF',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontWeight: 400,
          lineHeight: 1.35,
          fontSize: 'clamp(10px, min(2.2vw, 2.8vh), 20px)',
          minHeight: 'clamp(1.6em, min(4vh, 3vw), 2.7em)',
        }}
      >
        {/* {displayedText}
        {isTyping && (
          <span
            style={{ display: 'inline-block', width: '2px', height: '1em', background: '#fff', marginLeft: '2px', verticalAlign: 'middle', animation: 'blink 0.7s step-end infinite' }}
          />
        )} */}
        {text}
      </p>

      <style>{`
        @media (max-width: 639px) {
          .dialogue-box-container {
            width: 100% !important;
          }
          .dialogue-character-name {
            font-size: clamp(15px, 4.5vh, 22px) !important;
          }
          .dialogue-question-text {
            font-size: clamp(14px, 3.4vh, 17px) !important;
            max-height: clamp(64px, 16vh, 120px);
            overflow-y: auto;
            padding-right: 4px;
          }
          .dialogue-question-text::-webkit-scrollbar {
            width: 4px;
          }
          .dialogue-question-text::-webkit-scrollbar-thumb {
            background: rgba(165, 255, 245, 0.35);
            border-radius: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default DialogueBox;